import React from 'react'

import {useFormik} from 'formik'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'

import * as modalAction from '../../../actions/modal'

import useKeyPress from '../../../../../commons/src/hooks/use-key-press'
import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import useDispatch from '../../../hooks/use-dispatch'
import * as providerInfoApi from '../../../services/api/provider-info'
import {toTouchedFormData, toValidationSchema} from '../../utils/form'

import {ModalMeta, ProviderInfo} from '../../../types'

import Button from '../../../../../commons/src/components/button'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Modal from '../../../../../commons/src/components/modal'
import NumberInputField from '../../../../../commons/src/components/number-input-field'
import Paragraph from '../../../../../commons/src/components/paragraph'

import {
  SingleFormData,
  formDataToPartialRequest,
  initialValues,
  validationSchema,
} from '../../provider-info/edit-dimensions-partial'

type Props = {
  meta: ModalMeta
  providerInfos: Array<ProviderInfo>
  onDone?: () => void
}

const AddProviderInfoDimensionsModal = ({meta, providerInfos, onDone = noop}: Props) => {
  const dispatch = useDispatch()

  const formik = useFormik<SingleFormData>({
    initialValues,
    validationSchema: toValidationSchema(validationSchema),
    onSubmit: async (values, actions) => {
      const touchedValues = toTouchedFormData(values, initialValues) as Partial<SingleFormData>
      const providerInfoRequest = formDataToPartialRequest(touchedValues)

      try {
        const providerInfoIds = providerInfos.map(info => info.id)
        await providerInfoApi.patchMultiple(providerInfoRequest, providerInfoIds)

        dispatch(modalAction.close())
        onDone()
      } catch (error) {
        actions.setFieldError('formikGeneral', (error as Error).message)
      }
    },
  })

  useKeyPress(
    preventDoubleClick(() => formik.handleSubmit()),
    'Enter'
  )

  return (
    <Modal
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Add dimensions" size="l" />}
      onClose={() => dispatch(modalAction.close())}
      buttons={
        <>
          <Button hierarchy="ghost" onClick={() => dispatch(modalAction.close())}>
            Cancel
          </Button>
          <Button
            disabled={formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))}
            onClick={preventDoubleClick(() => formik.handleSubmit())}
          >
            Save
          </Button>
        </>
      }
    >
      <form onSubmit={formik.handleSubmit}>
        {formik.errors.formikGeneral && <Paragraph error>{formik.errors.formikGeneral}</Paragraph>}
        <Headline size="s" label="Min dimensions" classNames={['u-margin-bottom']} />
        <FormRow layout="thirds">
          <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.minimumDimensionsX}>
            <NumberInputField
              allowDecimals
              name="minimumDimensionsX"
              value={formik.values.minimumDimensionsX}
              prefix="X"
              suffix="mm"
              error={formik.submitCount > 0 && !!formik.errors.minimumDimensionsX}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
          <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.minimumDimensionsY}>
            <NumberInputField
              allowDecimals
              name="minimumDimensionsY"
              value={formik.values.minimumDimensionsY}
              prefix="Y"
              suffix="mm"
              error={formik.submitCount > 0 && !!formik.errors.minimumDimensionsY}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
          <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.minimumDimensionsZ}>
            <NumberInputField
              allowDecimals
              name="minimumDimensionsZ"
              value={formik.values.minimumDimensionsZ}
              prefix="Z"
              suffix="mm"
              error={formik.submitCount > 0 && !!formik.errors.minimumDimensionsZ}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
        <Headline size="s" label="Max dimensions" classNames={['u-margin-bottom']} />
        <FormRow layout="thirds">
          <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.maximumDimensionsX}>
            <NumberInputField
              allowDecimals
              name="maximumDimensionsX"
              value={formik.values.maximumDimensionsX}
              prefix="X"
              suffix="mm"
              error={formik.submitCount > 0 && !!formik.errors.maximumDimensionsX}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
          <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.maximumDimensionsY}>
            <NumberInputField
              allowDecimals
              name="maximumDimensionsY"
              value={formik.values.maximumDimensionsY}
              prefix="Y"
              suffix="mm"
              error={formik.submitCount > 0 && !!formik.errors.maximumDimensionsY}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
          <FieldNote type="error" note={formik.submitCount > 0 && formik.errors.maximumDimensionsZ}>
            <NumberInputField
              allowDecimals
              name="maximumDimensionsZ"
              value={formik.values.maximumDimensionsZ}
              prefix="Z"
              suffix="mm"
              error={formik.submitCount > 0 && !!formik.errors.maximumDimensionsZ}
              onChange={(value, name) => formik.setFieldValue(name as string, value)}
              onBlur={formik.handleBlur}
            />
          </FieldNote>
        </FormRow>
      </form>
    </Modal>
  )
}

export default AddProviderInfoDimensionsModal
